import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { FileText, Shield, Archive } from 'lucide-react';

export const TaxTab = ({
  taxYear, setTaxYear, generateTaxReport,
  downloadTaxPacket, downloadingPacket, taxReport,
  checkCompliance, complianceReport,
}) => (
  <div className="space-y-8" data-testid="tax-tab">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <Card className="card-tech p-6">
        <h3 className="text-xl font-bold text-green-400 mb-4"><FileText className="w-5 h-5 inline mr-2" /> Generate Tax Report</h3>
        <p className="text-green-300 text-sm mb-4">IRS-compliant tax report with income, expenses, and monthly breakdown.</p>
        <div className="flex gap-3 flex-wrap">
          <Input value={taxYear} onChange={e => setTaxYear(parseInt(e.target.value))} type="number" className="bg-black/50 border-green-500/30 text-green-300 w-32" data-testid="tax-year-input" />
          <Button onClick={generateTaxReport} className="btn-primary" data-testid="generate-tax-button">Generate Report</Button>
          <Button onClick={downloadTaxPacket} disabled={downloadingPacket} className="btn-secondary" data-testid="download-tax-packet-button">
            <Archive className="w-4 h-4 mr-2" />
            {downloadingPacket ? 'Building…' : 'Download Full Tax Packet (.zip)'}
          </Button>
        </div>
        <p className="text-green-500/60 text-xs mt-3">
          Packet contains tax_report.json, transactions.csv, staff_1099_nec.csv & README. Hand to your CPA.
        </p>
      </Card>

      <Card className="card-tech p-6">
        <h3 className="text-xl font-bold text-green-400 mb-4"><Shield className="w-5 h-5 inline mr-2" /> Legal Compliance Check</h3>
        <p className="text-green-300 text-sm mb-4">Verify your business meets all legal and ethical requirements.</p>
        <Button onClick={checkCompliance} className="btn-primary" data-testid="compliance-check-button">Run Compliance Check</Button>
      </Card>
    </div>

    {taxReport && (
      <Card className="card-tech p-6" data-testid="tax-report-display">
        <h3 className="text-xl font-bold text-green-400 mb-6">Tax Report: {taxReport.tax_year}</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="p-4 bg-green-500/10 rounded border border-green-500/20">
            <p className="text-green-400 text-sm">Gross Revenue</p>
            <p className="text-2xl font-bold text-green-500">${taxReport.income_summary.total_gross_revenue.toFixed(2)}</p>
          </div>
          <div className="p-4 bg-green-500/10 rounded border border-green-500/20">
            <p className="text-green-400 text-sm">Staff Expenses</p>
            <p className="text-2xl font-bold text-yellow-400">${taxReport.expenses_summary.staff_compensation.toFixed(2)}</p>
          </div>
          <div className="p-4 bg-green-500/10 rounded border border-green-500/20">
            <p className="text-green-400 text-sm">Net Income</p>
            <p className="text-2xl font-bold text-green-500">${taxReport.net_income.toFixed(2)}</p>
          </div>
        </div>
        <div className="mb-6">
          <h4 className="text-green-400 font-semibold mb-2">Sales Breakdown</h4>
          <p className="text-green-300">Basic: {taxReport.income_summary.basic_transactions} sales (${taxReport.income_summary.basic_tier_revenue.toFixed(2)})</p>
          <p className="text-green-300">Pro: {taxReport.income_summary.pro_transactions} sales (${taxReport.income_summary.pro_tier_revenue.toFixed(2)})</p>
        </div>
        <div className="mb-6">
          <h4 className="text-green-400 font-semibold mb-2">Required Tax Forms</h4>
          {taxReport.tax_forms_needed.map((form, idx) => (
            <p key={form || idx} className="text-green-300 text-sm">- {form}</p>
          ))}
        </div>
        <div>
          <h4 className="text-green-400 font-semibold mb-2">Compliance Notes</h4>
          {taxReport.compliance_notes.map((note, idx) => (
            <p key={note || idx} className="text-green-300 text-sm">- {note}</p>
          ))}
        </div>
      </Card>
    )}

    {complianceReport && (
      <Card className="card-tech p-6" data-testid="compliance-report-display">
        <h3 className="text-xl font-bold text-green-400 mb-6">
          Legal Compliance Status: {complianceReport.is_compliant ? '✅ COMPLIANT' : '⚠️ REVIEW NEEDED'}
        </h3>
        <div className="space-y-3">
          {Object.entries(complianceReport.checks).map(([key, check]) => (
            <div key={key} className="flex items-center justify-between p-3 bg-green-500/10 rounded border border-green-500/20">
              <span className="text-green-300">{key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</span>
              <span className={`px-3 py-1 rounded text-xs font-bold ${check.status === 'PASS' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                {check.status}
              </span>
            </div>
          ))}
        </div>
        <div className="mt-4 p-4 bg-green-500/5 rounded border border-green-500/10">
          <p className="text-green-400 font-semibold mb-2">Business Model</p>
          <p className="text-green-300">{complianceReport.business_model}</p>
          <p className="text-green-300">{complianceReport.legal_structure}</p>
        </div>
      </Card>
    )}
  </div>
);
